/**
 * "AI import – not in SWI yet": AI drawings waiting on a person, on the order and
 * designers screens, per order or sub-unit. Reads GET /api/ai/review/:orderId.
 *
 * - Saved without SWI: the drawing is in the CRM with warnings but has no SWI jobs,
 *   so the Flashing tab and PDF do not list it. "Edit & send to SWI" opens the normal
 *   editor exactly as a drawing card's Edit does (DrawingDetailsTab handleEdit); saving
 *   there sends it to SWI and it moves to the drawing cards.
 * - Needs drawing: nothing could be saved. "Draw it" opens the screen's Add a Design
 *   page (not pre-filled); "Done – remove" hides the card once it is drawn.
 *
 * Renders nothing when there is nothing waiting.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Button } from '@mui/material';
import axios from 'axios';
import swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, tokenManager } from '../config/api.config';
import { logger } from '../utils/logger';
import PreviewCanvas from '../Pages/DrawingComponents/PreviewThumbnail';

// While an import is still saving, look again this often.
const POLL_WHILE_IMPORTING_MS = 4000;

const authHeaders = () => ({ 'x-access-token': tokenManager.getToken(), Accept: 'application/json' });

const cardStyle = {
  border: '1px solid #f0ad4e',
  borderRadius: 6,
  background: '#fff',
  padding: 10,
  width: 300,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const tagsOf = (rows) => (rows || []).map((r) => r.tag).filter(Boolean).join(', ');
const qtyOf = (rows) => (rows || []).map((r) => `${r.qty} × ${r.length ? (r.length / 1000).toFixed(3) : '–'} m`).join(', ');

// orderId: Mongo _id. orderNumber: the order or sub-unit being viewed. canEdit: same
// rule as the drawing cards' Edit/Delete on that screen. currentPage: 'orders' or 'designers'.
// drawLink: { path, state } of that screen's "Add a Design" button, or null when the
// button is hidden there; "Draw it" goes where it goes (nothing is pre-filled).
const AiImportReviewSection = ({ orderId, orderNumber = null, canEdit = false, currentPage = 'orders', drawLink = null }) => {
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!orderId) return null;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/ai/review/${orderId}`, {
        headers: authHeaders(),
        params: orderNumber ? { orderNumber } : undefined,
      });
      const data = res.data?.data || null;
      setReview(data);
      return data;
    } catch (err) {
      logger.error('[AiImportReviewSection] could not load AI drawings waiting on review', err);
      return null;
    }
  }, [orderId, orderNumber]);

  useEffect(() => {
    let stopped = false;
    let timer = null;
    setReview(null);

    const tick = async () => {
      const data = await load();
      if (!stopped && data?.importRunning) timer = setTimeout(tick, POLL_WHILE_IMPORTING_MS);
    };

    // An import finished or a drawing changed on this page: look again.
    const onDrawingsChanged = (e) => {
      const changedId = e?.detail?.orderId;
      if (changedId && String(changedId) !== String(orderId)) return;
      if (timer) clearTimeout(timer);
      tick();
    };
    window.addEventListener('flashing-drawings-changed', onDrawingsChanged);

    tick();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('flashing-drawings-changed', onDrawingsChanged);
    };
  }, [orderId, load]);

  // Same steps as a drawing card's Edit (DrawingDetailsTab handleEdit), so the editor
  // opens this drawing exactly as it opens a card. Keep the two in step.
  const openEditor = async (card) => {
    setBusyId(String(card.templateId));
    try {
      let order = null;
      try {
        const res = await axios.get(`${API_BASE_URL}/api/orders/id/${orderId}`);
        order = res.data;
        if (order?.order_unique_id) localStorage.setItem('orderNumber', order.order_unique_id);
        if (order?.order_customer_id) localStorage.setItem('customerId', order.order_customer_id);
        if (order?.order_customer_name) localStorage.setItem('customerName', order.order_customer_name);
      } catch (err) {
        swal.fire({ text: 'Failed to fetch order data. Please try again.', icon: 'error' });
        return;
      }

      const editState = { ...card };
      editState.orderNumber = card.orderNumber || order?.order_unique_id;
      editState.customerId = order?.order_customer_id || card.customerId;
      editState.customerName = order?.order_customer_name || card.customerName;
      editState.orderId = orderId;
      editState.promiseDate = order?.order_delivery_date_str || order?.order_delivery_date;
      editState.enteredDate = order?.created_str || order?.created;

      try {
        const templateRes = await axios.get(`${API_BASE_URL}/api/templates/${card.templateId}`);
        const t = templateRes.data?.data || templateRes.data;
        if (t) {
          editState.flipH = t.flipH || false;
          editState.flipV = t.flipV || false;
          editState.firstSegmentAngle = t.firstSegmentAngle;
          editState.labelOffsets = t.labelOffsets;
          editState.splitLabelOffsets = t.splitLabelOffsets;
          editState.segmentAbsoluteAngles = t.segmentAbsoluteAngles || [];
          editState.startFoldGap = t.startFoldGap || 0;
          editState.endFoldGap = t.endFoldGap || 0;
          editState.previewUrl = t.previewUrl;
          editState.previewFarUrl = t.previewFarUrl;
          editState.previewNearUrl = t.previewNearUrl;
          editState.preview = t.preview;
          editState.previewFar = t.previewFar;
          editState.previewNear = t.previewNear;
          editState.lengths = t.lengths;
          editState.farLengths = t.lengths;
          editState.angles = t.angles;
          editState.farAngles = t.angles;
          editState.nearLengths = t.nearLengths;
          editState.nearAngles = t.nearAngles;
          editState.isTaper = t.isTaper;
          editState.direction = t.direction;
          editState.reverseColor = t.reverseColor;
          editState.girth = t.girth;
        }
      } catch (err) {
        logger.warn('[AiImportReviewSection] could not fetch the template, opening with the list data', err);
      }

      editState.isEdit = true;
      editState.isNewFromCanvas = false;
      editState.previousPage = currentPage;
      navigate(`/select-materials-simplified?templateId=${card.templateId}`, { state: editState, preventScrollReset: false });
    } finally {
      setBusyId(null);
    }
  };

  const deleteDrawing = async (card) => {
    const result = await swal.fire({
      title: 'Delete this AI drawing?',
      text: 'It was never sent to SWI, so only the CRM copy is deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d22530',
      confirmButtonText: 'Yes, Delete',
    });
    if (!result.isConfirmed) return;
    setBusyId(String(card.templateId));
    try {
      await axios.delete(`${API_BASE_URL}/api/ai/review/template/${card.templateId}`, { headers: authHeaders() });
      window.dispatchEvent(new CustomEvent('flashing-drawings-changed', { detail: { orderId } }));
    } catch (err) {
      swal.fire({ text: err.response?.data?.error || 'Delete failed', icon: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (item) => {
    setBusyId(item.drawingRef);
    try {
      await axios.post(`${API_BASE_URL}/api/ai/review/dismiss`,
        { commitId: item.commitId, drawingRef: item.drawingRef },
        { headers: authHeaders() });
      await load();
      // The banner's "to draw by hand" count. Not 'flashing-drawings-changed': nothing
      // on the Flashing PDF changed.
      window.dispatchEvent(new CustomEvent('ai-import-review-changed', { detail: { orderId } }));
    } catch (err) {
      swal.fire({ text: err.response?.data?.error || 'Could not dismiss', icon: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const saved = review?.savedWithoutSwi || [];
  const toDraw = review?.needsDrawing || [];
  if (saved.length === 0 && toDraw.length === 0) return null;

  return (
    <Alert severity="warning" icon={false} sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        AI import – {saved.length + toDraw.length} drawing{saved.length + toDraw.length === 1 ? '' : 's'} NOT in SWI yet
        {saved.length > 0 ? ' · fix each one and save it to send it to SWI' : ''}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {saved.map((card) => {
          const busy = busyId === String(card.templateId);
          return (
            <div key={String(card.templateId)} style={cardStyle}>
              <div style={{ fontWeight: 600 }}>
                {card.sheetNumber ? `Drawing ${card.sheetNumber}` : card.name || 'AI drawing'}
                {tagsOf(card.materialRows) ? ` · Tag ${tagsOf(card.materialRows)}` : ''}
                {card.isTaper ? ' · Taper' : ''}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', background: '#fafafa' }}>
                <PreviewCanvas
                  lines={(card.lengths || []).map(Number)}
                  angles={card.angles || []}
                  direction={card.direction || 'Right'}
                  firstSegmentAngle={card.firstSegmentAngle}
                  flipH={card.flipH}
                  flipV={card.flipV}
                  reverseColor={card.reverseColor}
                  width={260}
                  height={130}
                />
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, color: '#8a5300', fontSize: 13 }}>
                {(card.aiImportIssues || []).map((w, i) => (
                  <li key={`${w.code}-${i}`}>{w.message}</li>
                ))}
              </ul>
              <div style={{ fontSize: 13 }}>
                {[card.material, card.color].filter(Boolean).join(' · ')}
                {qtyOf(card.materialRows) ? ` · ${qtyOf(card.materialRows)}` : ''}
              </div>
              {canEdit && (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <Button size="small" variant="contained" disabled={busy} onClick={() => openEditor(card)}>
                    {busy ? 'Opening…' : 'Edit & send to SWI'}
                  </Button>
                  <Button size="small" color="error" disabled={busy} onClick={() => deleteDrawing(card)}>
                    Delete
                  </Button>
                </div>
              )}
            </div>
          );
        })}

        {toDraw.map((item) => {
          const busy = busyId === item.drawingRef;
          return (
            <div key={item.drawingRef} style={{ ...cardStyle, borderStyle: 'dashed', borderColor: '#d22530' }}>
              <div style={{ fontWeight: 600 }}>
                {item.sheetNumber ? `Drawing ${item.sheetNumber}` : item.name || 'AI drawing'}
                {item.tags?.length ? ` · Tag ${item.tags.join(', ')}` : ''}
              </div>
              <div style={{ color: '#a01e27', fontSize: 13 }}>{item.reason}</div>
              <div style={{ fontSize: 13 }}>
                {[item.material, item.colour].filter(Boolean).join(' · ')}
                {item.qty?.length ? ` · ${item.qty.join(', ')}` : ''}
              </div>
              {(drawLink || canEdit) && (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  {drawLink && (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busy}
                      onClick={() => navigate(drawLink.path, { state: drawLink.state })}
                    >
                      Draw it
                    </Button>
                  )}
                  {canEdit && (
                    <Button size="small" disabled={busy} onClick={() => dismiss(item)}>
                      {busy ? 'Saving…' : 'Done – remove'}
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Alert>
  );
};

export default AiImportReviewSection;
